import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import StaffSidebar from "../components/StaffSidebar";
import "../assets/css/results.css";

const LEVELS = ["ND1", "ND2", "HND1", "HND2", "Postgraduate", "Diploma"];

const isProduction = process.env.NODE_ENV === "production";
const API = isProduction
  ? process.env.REACT_APP_API_BASE_URL || "https://schools.luth.gov.ng"
  : "http://localhost:3003";

function computeGrade(score) {
  const n = Number(score);
  if (Number.isNaN(n)) return "";
  if (n >= 70) return "A";
  if (n >= 60) return "B";
  if (n >= 50) return "C";
  if (n >= 45) return "D";
  if (n >= 40) return "E";
  return "F";
}

function normalizeRole(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function getResultId(row) {
  return (
    row?.ResultID ??
    row?.ResultId ??
    row?.resultID ??
    row?.resultId ??
    row?.ID ??
    row?.Id ??
    null
  );
}

export default function ResultsEntryPage() {
  const programme = localStorage.getItem("programme") || "";
  const staffName =
    localStorage.getItem("fullName") ||
    localStorage.getItem("username") ||
    localStorage.getItem("staffName") ||
    "Unknown Staff";

  const rawRole =
    localStorage.getItem("role") || localStorage.getItem("userType") || "";
  const role = normalizeRole(rawRole);
  const isHOD = role === "hod";

  const roleLabel =
    role === "hod"
      ? "HOD"
      : role === "staff"
        ? "STAFF"
        : role === "student"
          ? "STUDENT"
          : role === "admin"
            ? "ADMIN"
            : "Unknown";

  const [selectedLevel, setSelectedLevel] = useState("");
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [students, setStudents] = useState([]);
  const [entries, setEntries] = useState({});
  const [selectedRows, setSelectedRows] = useState({});
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  const [approvingBulk, setApprovingBulk] = useState(false);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const loadCourses = async () => {
      if (!programme || !selectedLevel) {
        setCourses([]);
        setSelectedCourse(null);
        setStudents([]);
        setEntries({});
        setSelectedRows({});
        return;
      }

      try {
        setLoadingCourses(true);
        setMessage("");
        setSelectedCourse(null);
        setStudents([]);
        setEntries({});
        setSelectedRows({});

        const res = await axios.get(`${API}/api/staff/courses`, {
          params: {
            programme,
            level: selectedLevel,
            search: "",
          },
        });

        setCourses(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error(err);
        setMessage("Failed to load courses.");
      } finally {
        setLoadingCourses(false);
      }
    };

    loadCourses();
  }, [programme, selectedLevel]);

  useEffect(() => {
    const loadStudents = async () => {
      if (!selectedCourse) {
        setStudents([]);
        setEntries({});
        setSelectedRows({});
        return;
      }

      try {
        setLoadingStudents(true);
        setMessage("");

        const res = await axios.get(
          `${API}/api/courses/${selectedCourse.CourseID}/students`,
          { params: { level: selectedLevel } },
        );

        const rows = Array.isArray(res.data) ? res.data : [];
        setStudents(rows);

        const init = {};
        rows.forEach((s) => {
          init[s.MatricNumber] = {
            score: s.ExistingScore ?? "",
            grade: s.ExistingGrade ?? "",
            enteredBy: s.EnteredBy ?? "",
            status: s.Status ?? s.ResultStatus ?? "Provisional",
            resultId: getResultId(s),
          };
        });
        setEntries(init);
        setSelectedRows({});
      } catch (err) {
        console.error(err);
        setMessage("Failed to load students for this course.");
      } finally {
        setLoadingStudents(false);
      }
    };

    loadStudents();
  }, [selectedCourse, selectedLevel]);

  const visibleStudents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return students;

    return students.filter((s) => {
      const name = String(s.FullName || "").toLowerCase();
      const matric = String(s.MatricNumber || "").toLowerCase();
      return name.includes(term) || matric.includes(term);
    });
  }, [students, search]);

  const selectedVisibleStudents = useMemo(() => {
    return visibleStudents.filter((s) => selectedRows[s.MatricNumber]);
  }, [visibleStudents, selectedRows]);

  const stats = useMemo(() => {
    const total = students.length;
    const entered = students.filter((s) => Number(s.HasResult) === 1).length;
    const pending = total - entered;
    return { total, entered, pending };
  }, [students]);

  const handleScoreChange = (matricNo, value) => {
    const grade = value === "" ? "" : computeGrade(value);
    setEntries((prev) => ({
      ...prev,
      [matricNo]: {
        ...(prev[matricNo] || {}),
        score: value,
        grade,
      },
    }));
  };

  const handleGradeChange = (matricNo, value) => {
    setEntries((prev) => ({
      ...prev,
      [matricNo]: {
        ...(prev[matricNo] || {}),
        grade: value,
      },
    }));
  };

  const toggleRow = (matricNo) => {
    setSelectedRows((prev) => ({
      ...prev,
      [matricNo]: !prev[matricNo],
    }));
  };

  const toggleSelectAllVisible = (checked) => {
    const next = {};
    visibleStudents.forEach((s) => {
      next[s.MatricNumber] = checked;
    });
    setSelectedRows(next);
  };

  const allVisibleSelected =
    visibleStudents.length > 0 &&
    visibleStudents.every((s) => selectedRows[s.MatricNumber]);

  const someVisibleSelected =
    visibleStudents.some((s) => selectedRows[s.MatricNumber]) &&
    !allVisibleSelected;

  const refreshStudents = async () => {
    const refresh = await axios.get(
      `${API}/api/courses/${selectedCourse.CourseID}/students`,
      { params: { level: selectedLevel } },
    );

    const rows = Array.isArray(refresh.data) ? refresh.data : [];
    setStudents(rows);

    const init = {};
    rows.forEach((s) => {
      init[s.MatricNumber] = {
        score: s.ExistingScore ?? "",
        grade: s.ExistingGrade ?? "",
        enteredBy: s.EnteredBy ?? "",
        status: s.Status ?? "Provisional",
        resultId: getResultId(s),
      };
    });
    setEntries(init);
    setSelectedRows({});
  };

  const saveSelectedRows = async () => {
    if (!selectedCourse) return;

    if (selectedVisibleStudents.length === 0) {
      setMessage("Select at least one student first.");
      return;
    }

    try {
      setSavingBulk(true);
      setMessage("");

      for (const student of selectedVisibleStudents) {
        const matricNo = student.MatricNumber;
        const row = entries[matricNo] || {};
        const score = row.score;

        if (score === "" || score === null || score === undefined) {
          setMessage(`Score is required for ${student.FullName}.`);
          return;
        }

        const existing = Number(student.HasResult) === 1;

        if (existing && !isHOD) {
          setMessage("Only HOD can update already entered results.");
          return;
        }

        const payload = {
          matricNo,
          courseId: selectedCourse.CourseID,
          score,
          grade: row.grade || computeGrade(score),
          enteredBy: staffName,
          status: "Provisional",
        };

        if (existing) {
          await axios.put(`${API}/api/results`, payload, {
            headers: {
              "Content-Type": "application/json",
              "x-user-role": roleLabel,
              "x-user-name": staffName,
            },
          });
        } else {
          await axios.post(`${API}/api/results`, payload, {
            headers: {
              "Content-Type": "application/json",
              "x-user-role": roleLabel,
              "x-user-name": staffName,
            },
          });
        }
      }

      setMessage(
        isHOD
          ? "Selected results saved/updated successfully."
          : "Selected results saved successfully.",
      );
      await refreshStudents();
    } catch (err) {
      console.error(err);
      setMessage(
        err.response?.data?.msg ||
          err.response?.data?.detail ||
          "Failed to save selected results.",
      );
    } finally {
      setSavingBulk(false);
    }
  };

  const approveSelectedRows = async () => {
    if (!isHOD) {
      setMessage("Only HOD can approve results.");
      return;
    }

    const rowsToApprove = selectedVisibleStudents.filter(
      (s) => Number(s.HasResult) === 1,
    );

    if (rowsToApprove.length === 0) {
      setMessage("Select at least one saved result to approve.");
      return;
    }

    try {
      setApprovingBulk(true);
      setMessage("");

      for (const student of rowsToApprove) {
        await axios.put(
          `${API}/api/results/approve`,
          {
            matricNo: student.MatricNumber,
            courseId: selectedCourse.CourseID,
          },
          {
            headers: {
              "Content-Type": "application/json",
              "x-user-role": "HOD",
              "x-user-name": staffName,
            },
          },
        );
      }

      setMessage("Selected results approved successfully.");
      await refreshStudents();
    } catch (err) {
      console.error(err);
      setMessage(
        err.response?.data?.msg || "Failed to approve selected results.",
      );
    } finally {
      setApprovingBulk(false);
    }
  };

  const courseInfo = selectedCourse
    ? `${selectedCourse.CourseCode} • ${selectedCourse.CourseTitle} • ${selectedCourse.Level} • ${selectedCourse.Semester || ""}`
    : "Select a level, then choose a course";

  return (
    <div className="results-page">
      <StaffSidebar />

      <main className="results-entry-main">
        <div className="results-hero">
          <div>
            <h1>Results Entry</h1>
            <p>
              {programme || "No programme selected"} · Role: {roleLabel} ·
              Staff: {staffName}
            </p>
          </div>

          <div className="hero-pill">
            {selectedCourse ? "Course loaded" : "Awaiting selection"}
          </div>
        </div>

        <section className="results-panel">
          <div className="panel-grid">
            <div className="panel-card">
              <label>Level</label>
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
              >
                <option value="">-- Select Level --</option>
                {LEVELS.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>

            <div className="panel-card">
              <label>Course</label>
              <select
                value={selectedCourse?.CourseID || ""}
                onChange={(e) => {
                  const cid = Number(e.target.value);
                  const found = courses.find((c) => Number(c.CourseID) === cid);
                  setSelectedCourse(found || null);
                  setSearch("");
                }}
                disabled={!selectedLevel || loadingCourses}
              >
                <option value="">
                  {loadingCourses
                    ? "Loading courses..."
                    : "-- Select Course --"}
                </option>
                {courses.map((c) => (
                  <option key={c.CourseID} value={c.CourseID}>
                    {c.CourseCode} - {c.CourseTitle}
                  </option>
                ))}
              </select>

              {selectedLevel && !loadingCourses && courses.length === 0 && (
                <div className="empty-state">
                  No courses found for this level.
                </div>
              )}
            </div>

            <div className="panel-card">
              <label>Search student</label>
              <input
                type="text"
                placeholder="Name or matric number"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={!selectedCourse}
              />
            </div>
          </div>

          <div className="stats-row">
            <div className="stat-box">
              <span>Total Students</span>
              <strong>{stats.total}</strong>
            </div>
            <div className="stat-box">
              <span>Results Entered</span>
              <strong>{stats.entered}</strong>
            </div>
            <div className="stat-box">
              <span>Pending</span>
              <strong>{stats.pending}</strong>
            </div>
            <div className="stat-box wide">
              <span>Selected Course</span>
              <strong>{courseInfo}</strong>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <button
              type="button"
              className="save-btn"
              onClick={saveSelectedRows}
              disabled={savingBulk || selectedVisibleStudents.length === 0}
            >
              {savingBulk
                ? "Saving..."
                : isHOD
                  ? "Update/Save Selected"
                  : "Save Selected"}
            </button>

            {isHOD && (
              <button
                type="button"
                className="approve-btn"
                onClick={approveSelectedRows}
                disabled={approvingBulk || selectedVisibleStudents.length === 0}
              >
                {approvingBulk ? "Approving..." : "Approve Selected"}
              </button>
            )}
          </div>

          {message && <div className="message-box">{message}</div>}

          {!selectedLevel && (
            <div className="empty-state">
              Select a level to load the available courses.
            </div>
          )}

          {selectedLevel && !selectedCourse && !loadingCourses && (
            <div className="empty-state">
              Select a course to load the students registered for that level.
            </div>
          )}

          {loadingStudents && (
            <div className="empty-state">Loading students...</div>
          )}

          {selectedCourse && !loadingStudents && visibleStudents.length > 0 && (
            <div className="table-wrap">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someVisibleSelected;
                        }}
                        onChange={(e) =>
                          toggleSelectAllVisible(e.target.checked)
                        }
                      />
                    </th>
                    <th>Student</th>
                    <th>Matric No</th>
                    <th>Level</th>
                    <th>Session</th>
                    <th>Score</th>
                    <th>Grade</th>
                    <th>Entered By</th>
                    <th>Result Status</th>
                  </tr>
                </thead>

                <tbody>
                  {visibleStudents.map((s) => {
                    const existing = Number(s.HasResult) === 1;
                    const locked = existing && !isHOD;
                    const statusText =
                      entries[s.MatricNumber]?.status ??
                      s.Status ??
                      "Provisional";

                    return (
                      <tr
                        key={s.MatricNumber}
                        className={existing ? "saved-row" : ""}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={!!selectedRows[s.MatricNumber]}
                            onChange={() => toggleRow(s.MatricNumber)}
                          />
                        </td>
                        <td>{s.FullName}</td>
                        <td>{s.MatricNumber}</td>
                        <td>{s.StudentLevel || selectedLevel}</td>
                        <td>{s.Session || "-"}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={entries[s.MatricNumber]?.score ?? ""}
                            onChange={(e) =>
                              handleScoreChange(s.MatricNumber, e.target.value)
                            }
                            readOnly={locked}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={entries[s.MatricNumber]?.grade ?? ""}
                            onChange={(e) =>
                              handleGradeChange(s.MatricNumber, e.target.value)
                            }
                            readOnly={locked}
                          />
                        </td>
                        <td>{s.EnteredBy || "-"}</td>
                        <td>
                          <span
                            className={`badge ${
                              String(statusText).toLowerCase() === "approved"
                                ? "badge-approved"
                                : "badge-provisional"
                            }`}
                          >
                            {statusText}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {selectedCourse &&
            !loadingStudents &&
            visibleStudents.length === 0 && (
              <div className="empty-state">
                No students found for this course and level.
              </div>
            )}
        </section>
      </main>
    </div>
  );
}
